import { services } from 'services/container';

const units = ['Kb', 'Mb', 'Gb', 'Tb'];
const step = 1024;

export const fileSizeStr = (sizeBytes: number, decimals = 1) => {
    if (Math.abs(sizeBytes) < step) {
        return sizeBytes + ' b';
    }

    let unit = -1;
    const r = 10 ** decimals;

    do {
        sizeBytes /= step;
        ++unit;
    } while (Math.round(Math.abs(sizeBytes) * r) / r >= step && unit < units.length - 1);

    return `${sizeBytes.toFixed(decimals)} ${units[unit]}`;
};

export const downscaleImage = (src: Blob, maxResolution = 1920) =>
    new Promise<Blob>(async (res, rej) => {
        const fileType = src?.type;

        if (!fileType?.startsWith('image/')) {
            rej('The provided object is not an image');
        }

        setTimeout(() => rej('Image downscaling timed out'), 10000);

        const img = await new Promise<HTMLImageElement>((res) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    res(img);
                };
                img.src = reader.result as string;
            };
            reader.readAsDataURL(src);
        });

        const scale = maxResolution / Math.max(img.width, img.height);

        if (scale >= 1) {
            return res(src);
        }

        const [w, h] = [Math.floor(img.width * scale), Math.floor(img.height * scale)];

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((result) => {
            res(result);
        }, fileType);
    });

export const downloadFileFromUrl = (url: string, fileName?: string) => {
    if (!url) throw 'Failed to download file (No file URL was provided)';

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || url.split('/').at(-1);

    a.click();
    a.remove();
};

export const blobToDataUrl = (blob: Blob) =>
    new Promise<string>((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = () => rej(reader.error);
        reader.onabort = () => rej('Read aborted');
        reader.readAsDataURL(blob);
    });

export const isUsableImageSource = (src: string) => {
    const result = new Promise<boolean>((resolve) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = src;
    });
    return result;
};

export const attemptLoadImage = async (src: string) => {
    const imageSrc: string = services.vo.getThumbUrl(src);
    if (await isUsableImageSource(imageSrc)) {
        return imageSrc;
    }
    return null;
};

export const safeDecodeURIComponent = (uri: string) => {
    return uri.replace(/(?:%[a-f0-9]+)+/gim, (s) => {
        try {
            return decodeURIComponent(s);
        } catch (ex) {
            return s;
        }
    });
};
